import { useLanguage } from "@/i18n";
import { Reveal, Chapter } from "./shared";

const IMAGES = {
  dostava: "/assets/tiger-city.jpg",
  amperi: "/assets/amperi-ranger.jpg",
  tiger: "/assets/amperi-tiger-blue.png",
  batmat: "/assets/catl-battery.jpg",
  storm: "/assets/newbot-storm.jpg",
  rugged: "/assets/amperi-donk.jpg",
  tuktuk: "https://margtor.africa/assets/img/models/three-seater.jpg",
};

const SPANS = ["md:col-span-6", "md:col-span-4", "md:col-span-2", "md:col-span-2", "md:col-span-2", "md:col-span-2", "md:col-span-6"];

export const Projects = () => {
  const { t } = useLanguage();
  return (
    <section id="projects" className="scroll-mt-24 bg-moss" data-testid="projects-section">
      <div className="mx-auto max-w-7xl px-5 py-24 lg:px-10 lg:py-32">
        <Chapter num="04" label={t.projects.chapter.split("/ ")[1]} />
        <Reveal>
          <h2 data-testid="projects-heading" className="mt-12 font-display text-3xl font-extrabold tracking-tight text-neutral-950 sm:text-4xl lg:text-5xl">
            {t.projects.heading}
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-6">
          {t.projects.items.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.08} className={SPANS[i]}>
              <article
                data-testid={`project-card-${p.id}`}
                className="group flex h-full flex-col overflow-hidden rounded-[1.5rem] border border-neutral-200 bg-white transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-[0_32px_70px_-34px_rgba(6,78,59,0.4)]"
              >
                <div className="overflow-hidden">
                  <img
                    src={IMAGES[p.id]}
                    alt={p.name}
                    loading="lazy"
                    className={`w-full transition-transform duration-700 group-hover:scale-[1.06] ${["amperi", "tiger", "rugged", "storm"].includes(p.id) ? "bg-white object-contain p-5" : "object-cover"} ${p.id === "dostava" ? "h-44" : "h-52"}`}
                  />
                </div>
                <div className="flex flex-1 flex-col p-7">
                  <span className="w-fit rounded-full bg-brand-100 px-3 py-1 font-mono text-[10px] font-medium uppercase tracking-[0.18em] text-brand-700">
                    {p.badge}
                  </span>
                  <h3 className="mt-4 font-display text-2xl font-bold text-neutral-950">{p.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-500">{p.desc}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-16" data-testid="specs-block">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h3 data-testid="specs-heading" className="font-display text-2xl font-extrabold tracking-tight text-neutral-950 sm:text-3xl">
                  {t.specs.heading}
                </h3>
                <p className="mt-2 max-w-xl text-sm text-neutral-500">{t.specs.sub}</p>
              </div>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/vehicles`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="specs-json-link"
                className="font-mono text-xs font-medium text-brand-700 underline decoration-brand-200 underline-offset-4 transition-colors hover:text-brand-500"
              >
                {t.specs.jsonLink}
              </a>
            </div>
            <div className="mt-7 overflow-x-auto rounded-[1.5rem] border border-neutral-200 bg-white shadow-[0_24px_60px_-40px_rgba(44,56,28,0.35)]" data-testid="specs-table-wrapper">
              <table className="w-full min-w-[920px] text-left" data-testid="specs-table">
                <thead>
                  <tr className="border-b border-neutral-200 bg-moss">
                    {t.specs.cols.map((c, i) => (
                      <th key={i} className="px-5 py-4 font-mono text-[11px] font-medium uppercase tracking-[0.15em] text-neutral-500">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {t.specs.rows.map((row, i) => (
                    <tr key={i} data-testid={`specs-row-${i}`} className="border-b border-neutral-100 transition-colors duration-300 last:border-0 hover:bg-brand-50/70">
                      {row.map((cell, j) => (
                        <td key={j} className={j === 0 ? "px-5 py-4 font-display text-sm font-bold text-neutral-900" : "px-5 py-4 font-mono text-xs text-neutral-600"}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 font-mono text-[11px] text-neutral-400" data-testid="specs-note">{t.specs.note}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
